<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import EmailExistsModal from './modals/EmailExistsModal.vue'
import { AUTH_PROVIDERS } from './authProviders'
import { useLoginSection, type LoginSectionProps } from './useLoginSection'
import { apiPost } from '@/lib/http'
import { getIntlState } from '@/lib/intl'

const props = defineProps<LoginSectionProps>()

const emit = defineEmits<{
  (e: 'change', section: string): void
  (e: 'update:shared-data', data: Record<string, unknown>): void
}>()

const { changeSection, setDataItem } = useLoginSection(props, emit)

const showPassword = ref(false)
const error = ref('')
const credentials = reactive({ email: '', password: '' })
const submitting = ref(false)
const emailExists = ref(false)

const authUrls = computed(() => {
  const isIntl = getIntlState()
  return AUTH_PROVIDERS.filter(item => !(item.ruOnly && isIntl)).map(item => {
    const url = new URL(`/users/auth_${item.slug}`, window.location.href)
    const params = new URLSearchParams(window.location.search)
    params.set('type', props.sharedData.role === 1 ? 'teacher' : 'parent')
    url.search = params.toString()
    return { key: item.slug, url: url.toString() }
  })
})

const canChangeRole = computed(() => !props.query.has('type'))

onMounted(() => {
  if (props.sharedData.credentials) {
    credentials.email = props.sharedData.credentials.email
    credentials.password = props.sharedData.credentials.password
  }
})

async function submit() {
  if (submitting.value) return
  error.value = ''

  if (!credentials.email.trim() || !credentials.password.trim()) {
    await nextTick()
    error.value = 'Заполните все поля.'
    return
  }

  submitting.value = true
  const result = await apiPost('/users/request_validate_credentials', credentials)
  submitting.value = false

  if (result.failed && result.errors?.email?.[0]?.code === 'unique') {
    emailExists.value = true
  }

  if (result.failed) {
    const firstErrorKey = Object.keys(result.errors || {})[0]
    error.value = result.errors?.[firstErrorKey]?.[0]?.message || ''
    return
  }

  setDataItem('credentials', { ...credentials })
  changeSection(props.LoginSection.SignupProfileForm)
}

function changeRole() {
  setDataItem('role', undefined)
  changeSection(props.LoginSection.RoleSelect)
}
</script>

<template>
  <div class="signin-form">
    <h1 class="text-h3 text-center">Создать аккаунт</h1>

    <EmailExistsModal v-model="emailExists" :email="credentials.email" />

    <ul class="signin-form__oauth mt-4">
      <li v-for="provider in authUrls" :key="provider.key">
        <a :href="provider.url">
          <v-icon size="32">auth-providers/{{ provider.key }}</v-icon>
        </a>
      </li>
    </ul>

    <div class="signin-form__divider my-3">
      <hr />
      <span class="mx-1">Или</span>
      <hr />
    </div>

    <form @submit.prevent="submit">
      <!-- TODO: phone authentication -->
      <!-- <v-field label="Эл. почта или номер телефона"> -->
      <v-field label="Эл. почта" class="mt-3">
        <!-- <v-input placeholder="Введите вашу почту или телефон"></v-input> -->
        <v-input
          v-model.trim="credentials.email"
          placeholder="Введите вашу почту"
          type="text"
          name="email"
          autocomplete="email"
        />
      </v-field>

      <v-field label="Пароль" class="mt-2">
        <v-input
          v-model.trim="credentials.password"
          placeholder="Придумайте пароль"
          :type="showPassword ? 'text' : 'password'"
          name="password"
          :error="error"
          autocomplete="new-password"
        >
          <template #append>
            <v-icon
              class="signin-form__hide-btn"
              :class="showPassword && 'signin-form__hide-btn_active'"
              @click="showPassword = !showPassword"
            >
              mdi-eye
            </v-icon>
          </template>
        </v-input>
      </v-field>

      <v-button type="submit" mode="primary filled large block" class="mt-5" :disabled="submitting">
        Продолжить
      </v-button>

      <v-button v-if="canChangeRole" type="button" mode="outlined large block" class="mt-2" @click="changeRole">
        Назад
      </v-button>
    </form>
  </div>
</template>

<style lang="scss">
.signin-form {
  @include desktop {
    width: 320px !important;
    margin: 0 auto;
  }

  &__oauth {
    display: grid;
    grid-auto-flow: column;
    justify-content: center;
    grid-gap: 0 24px;

    & > li {
      width: 64px;
      height: 64px;
      background: $lightenGray;
      border-radius: 8px;

      & > a {
        display: block;
        width: 64px;
        height: 64px;
        padding: 16px;
      }
    }
  }

  &__divider {
    display: flex;
    align-items: center;
    color: $darkGray;

    & > hr {
      flex: 1;
      height: 1px;
      border: none;
      border-bottom: 1px solid $lightGray;
    }
  }

  &__hide-btn {
    cursor: pointer;
    color: $gray;
    transition: color 0.2s ease;

    &_active {
      color: $blue;
    }
  }
}
</style>
